import type { CouponDiscountType } from '@/api/repository/types/types';

/**
 * 세미나 쿠폰 표시 규칙 (docs/seminar-coupon-spec.md, seminar-coupon-impl-plan.md 0·2절).
 * 금액은 원 단위 그대로(반올림 없음). 정률 상한 90%, 실결제 최소 1,000원.
 */

export const MIN_PAYMENT_AMOUNT: number = 1000;
export const MAX_RATE_PERCENT: number = 90;
/** 플랫폼 수수료(부가세 포함) — 강사 예상 지급 미리보기용 */
export const INSTRUCTOR_COMMISSION_RATE: number = 0.15;

/** 10,000 이상이고 천 원 단위면 "1만" · "1.5만", 아니면 "5,000" */
export const shortMan = (value: number): string => {
    if (value >= 10000 && value % 1000 === 0) return `${value / 10000}만`;
    return value.toLocaleString();
};

/** "1만 원" · "5,000원" */
export const shortWon = (value: number): string => {
    const text: string = shortMan(value);
    return text.endsWith('만') ? `${text} 원` : `${text}원`;
};

export const won = (value: number): string => `${value.toLocaleString()}원`;

export interface CouponValueSource {
    discountType?: CouponDiscountType;
    discountValue?: number;
    maxDiscountAmount?: number | null;
}

/** 쿠폰 카드 왼쪽 72px 칸 — 큰 글자 */
export const couponAmountText = (c: CouponValueSource): string => {
    if (c.discountType === 'RATE') return `${c.discountValue ?? 0}%`;
    return shortWon(c.discountValue ?? 0);
};

/** 쿠폰 카드 왼쪽 72px 칸 — 작은 글자("정액" · "최대 1만" · "정률") */
export const couponAmountSub = (c: CouponValueSource): string => {
    if (c.discountType === 'RATE') {
        const max: number | null | undefined = c.maxDiscountAmount;
        if (!max) return '정률';
        const text: string = shortMan(max);
        return text.endsWith('만') ? `최대 ${text}` : `최대 ${text}원`;
    }
    return '정액';
};

/**
 * 정가 기준 할인액 — 서버 SeminarCouponCalculator.discount 와 같은 식(강사 쿠폰 미리보기용, 명치사 상한은 서버만 안다).
 * FIXED: min(값, 정가 − 1,000) / RATE: min(floor(정가 × % / 100), 최대 할인, 정가 − 1,000). 0 이하면 0.
 */
export const calcDiscount = (
    discountType: CouponDiscountType,
    discountValue: number,
    maxDiscountAmount: number | null | undefined,
    listPrice: number,
): number => {
    if (!listPrice || listPrice <= MIN_PAYMENT_AMOUNT || !discountValue || discountValue <= 0) return 0;
    const ceiling: number = listPrice - MIN_PAYMENT_AMOUNT;
    let discount: number = discountType === 'RATE'
        ? Math.floor((listPrice * Math.min(discountValue, MAX_RATE_PERCENT)) / 100)
        : discountValue;
    if (discountType === 'RATE' && maxDiscountAmount && maxDiscountAmount > 0) discount = Math.min(discount, maxDiscountAmount);
    discount = Math.min(discount, ceiling);
    return discount > 0 ? discount : 0;
};

/** 할인율(%) — 정가 대비, 내림 */
export const discountRate = (discount: number, listPrice: number): number =>
    listPrice > 0 ? Math.floor((discount / listPrice) * 100) : 0;

const toDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const date: Date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** "10월 14일까지" (withTime 이면 "10월 14일 23:00까지") */
export const untilText = (value?: string | null, withTime: boolean = false): string => {
    const date: Date | null = toDate(value);
    if (!date) return '';
    const base: string = `${date.getMonth() + 1}월 ${date.getDate()}일`;
    if (!withTime) return `${base}까지`;
    const hh: string = String(date.getHours()).padStart(2, '0');
    const mm: string = String(date.getMinutes()).padStart(2, '0');
    return `${base} ${hh}:${mm}까지`;
};

/** "~10/14" */
export const shortUntil = (value?: string | null): string => {
    const date: Date | null = toDate(value);
    return date ? `~${date.getMonth() + 1}/${date.getDate()}` : '';
};

/** 남은 날(오늘 0) — 서버 dday 가 있으면 그 값 */
export const ddayOf = (endsAt?: string | null, dday?: number | null): number | null => {
    if (typeof dday === 'number') return dday;
    const date: Date | null = toDate(endsAt);
    if (!date) return null;
    const today: Date = new Date();
    today.setHours(0, 0, 0, 0);
    const end: Date = new Date(date);
    end.setHours(0, 0, 0, 0);
    return Math.round((end.getTime() - today.getTime()) / 86400000);
};

/** 명치사 발행 쿠폰인지 — issuerType 이 있으면 그 값, 없으면 발행자 이름이 없거나 "명치사" */
export const isPlatformIssuer = (issuerName?: string | null, issuerType?: string | null): boolean => {
    if (issuerType) return issuerType === 'ADMIN';
    return !issuerName || issuerName === '명치사';
};

/** 서버가 배열 또는 {list:[...]} 로 줄 수 있다 — 어느 쪽이든 배열로 */
export const unwrapList = <T,>(result: unknown): T[] => {
    if (Array.isArray(result)) return result as T[];
    if (result && typeof result === 'object') {
        const record = result as { list?: unknown; coupons?: unknown; content?: unknown };
        if (Array.isArray(record.list)) return record.list as T[];
        if (Array.isArray(record.coupons)) return record.coupons as T[];
        if (Array.isArray(record.content)) return record.content as T[];
    }
    return [];
};

/**
 * 쿠폰 환불 복원 안내 — 서버 SeminarCouponService.onRefunded 규칙과 같게:
 * 폐강·강사 거절 환불은 항상 복원, 그 외에는 전액 환불이고 쿠폰의 "환불 시 복원" 설정일 때만(부분 환불은 복원 없음).
 * 결제 창·쿠폰 받기 카드·서버 결제 내역 안내(SeminarCouponService.refundNoticeOf)가 같은 문장을 쓴다.
 */
export const COUPON_NO_RESTORE_NOTICE: string = '환불돼도 쿠폰은 돌아오지 않아요. 폐강되거나 강사가 신청을 거절하면 돌려드려요.';
export const COUPON_RESTORE_NOTICE: string = '전액 환불되면 쿠폰은 다시 쓸 수 있어요.';

/** 취소 팝업 한 줄(서버 안내 문구가 없을 때) — 이번 취소로 쿠폰이 돌아오는지 */
export const cancelCouponLine = (couponName: string, restored: boolean): string =>
    restored ? `전액 환불이라 ${couponName} 쿠폰은 다시 쓸 수 있어요.` : `환불돼도 ${couponName} 쿠폰은 돌아오지 않아요.`;

/** 적용 범위 문구 — 지정 세미나면 "제목 전용", 전체면 "전체 유료 세미나" */
export const scopeText = (scopeLabel?: string | null, scope?: string | null): string => {
    if (!scopeLabel) return scope === 'SEMINAR' ? '지정 세미나 전용' : '전체 유료 세미나';
    return scope === 'SEMINAR' ? `${scopeLabel} 전용` : scopeLabel;
};

/** 대문자·숫자 8자리 코드 — 헷갈리는 0/O/1/I 는 뺀다 */
export const generateCouponCode = (): string => {
    const chars: string = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code: string = '';
    for (let i = 0; i < 8; i += 1) code += chars[Math.floor(Math.random() * chars.length)];
    return code;
};

/** 코드 입력 정리 — 대문자·숫자만, 최대 12자 */
export const sanitizeCouponCode = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);

/** 409/400 등 서버 오류 메시지 추출 */
export const errorMessageOf = (error: unknown, fallback: string): string => {
    const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
    return data?.message || fallback;
};
